/**
 * AuthContext – single source of truth for "who's signed in".
 *
 * Three states:
 *   - user === null   → still checking
 *   - user object     → authenticated
 *   - user === false  → not authenticated (after initial check)
 *
 * IMPORTANT: If the current URL already contains `#session_id=` (Emergent
 * Google OAuth callback), we SKIP the initial /auth/me probe so the callback
 * component can exchange the session first without racing us.
 *
 * Auth token handling — see `lib/api.js`:
 *   Login/register responses include `access_token`. We ALSO store it in
 *   localStorage so axios can attach it as a Bearer header. This keeps auth
 *   alive even when the preview is inside an iframe and the browser blocks
 *   cross-site cookies.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { http, apiErrorMessage, setStoredToken } from '@/lib/api';
import { scheduleEngagementNotifications } from '@/lib/nativeNotifications';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get('/auth/me');
      setUser(data);
      // On native, schedule daily reminders for authenticated users.
      scheduleEngagementNotifications();
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      // Let AuthCallback handle it.
      setUser(false);
      return;
    }
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await http.post('/auth/login', { email, password });
      if (data.access_token) setStoredToken(data.access_token);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiErrorMessage(e) };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await http.post('/auth/register', { name, email, password });
      if (data.access_token) setStoredToken(data.access_token);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: apiErrorMessage(e) };
    }
  };

  const logout = async () => {
    // Local sign-out must succeed even if the server is unreachable. We
    // swallow the network error and clear the token unconditionally.
    try {
      await http.post('/auth/logout');
    } catch {
      // Non-fatal — server-side cookie clear is a best effort.
    }
    setStoredToken('');
    setUser(false);
  };

  const patchUser = (patch) => setUser((u) => (u && typeof u === 'object' ? { ...u, ...patch } : u));

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh, setUser, patchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
